/*
 *   Copyright (c) 2026, CYBRA Corp. All rights reserved. 
 */

import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UserValidationDialogComponent } from "app/components/login-validation-dialog/user-validation-dialog.component";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { SimpleSuccessErrorDialogComponent, ERROR_DIALOG_TITLE, SUCCESS_DIALOG_TITLE } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { InstanceData } from "app/models/instance-data";
import { ReaderInstance } from "app/models/reader-instance";
import { ServiceInstance } from "app/models/service-instance";
import { AblenetService, ValidationInfoDto, AbleNetShipmentValidation_ValidationStateEnum, AbleNetShipmentValidation_AttemptDto, AbleNetValidation_AssetDetailDto, AbleNetReaderStateEnum } from "app/services/ablenet.service";
import { ReaderService } from "app/services/reader.service";


/** The delay between the API calls to refresh the information in the UI */
const RefreshDelayInMs = 1000;

/** Property Key on the AbleNet Shipment Validation Service's "Reader" Property which is used to specify the Reader that should be controlled. */
const ServicePropertyKey_Reader = "associated_reader";

/** Property Key on Reader Instance's which is used for the Reader's Status. */
const ReaderPropertyKey_ReaderStatus = "reader_status";

/** Function Key for the "Start Reader" Function to be performed on the Reader */
const ReaderFunctionKey_StartReader = "start";

/** Function Key for the "Stop Reader" Function to be performed on the Reader */
const ReaderFunctionKey_StopReader = "stop";

@Component({
  templateUrl: './ablenet.shipment-validation.dashboard.html',
  styleUrls: ['./ablenet.shipment-validation.dashboard.css']
})
export class AbleNetShipmentValidationDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) 
  indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog')
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

    /** Display the User Validation Dialog for Override option */
  @ViewChild('userValidationDialog')
  userValidationDialogComponent: UserValidationDialogComponent;

  /** Portal Instance which is performing the Shipment Validation and whose information is being shown */
  portal: ServiceInstance;

  /** Current State being reflected in the UI */
  state: ValidationInfoDto;

  /** Flag used to indicate if the component has been destroyed or not.  This is used by the background "Refresh Data" task to indicate when it should stop. */
  isDestroyed = false;

  /** Reader that is being controlled by the Portal */
  reader: ReaderInstance = null;

  /** Holds the current Reader State if it is known */
  readerState: AbleNetReaderStateEnum = null;

  /** Flag used to indicate if the Reader State has been retrieved or not. Used to disable some UI controls while the state is being retrieved.*/
  isReaderStateRetrieved: boolean = false;

  /** Flag used to indicate if any Reader or Order Actions are being performed.  This includes: [Start Reader, Stop Reader, Clear Order, Process Order] */
  isActionBeingPerformed: boolean = false;

  /** DTO to store th Shipment Validation Attempt Info */
  shipmentInfo: AbleNetShipmentValidation_AttemptDto = null;

   /** Flag to display the Override Dialog */
  displayOverrideDialog: boolean = false;

  /** Flag to keep track of the Override status */
  override : boolean = false;

  /** List of Roles allowed to override the validation  */
  overrideRoles: string[] = [];

  /** Flag to display the Override button */
  displayOverrideButton: boolean = false;

  /** Flag to indicate if this shipment is a Warranty Return */
  isAWarranty: boolean = false;

  /** Constructor */
  constructor(
    private ablenetService: AblenetService,
    private readerService: ReaderService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  /**
     * Initializes the component.  
     * Called automatically by the Angular Component Lifecycle.
     */
  ngOnInit(): void {
    console.log(`ngOnInit()`);
    /* 
     * Get the data that was resolved in the background and kickstart the 
     * "Refresh" task that keeps the UI up to date
     */
    this.route.data.subscribe((
      data: {
        portal: ServiceInstance,
        state: ValidationInfoDto,
      }
    ) => {
      // Update the portal / state values that were retrieved in the background
      this.portal = data?.portal;
      this.state = data?.state;
      this.overrideRoles = this.state?.attemptDto?.overrideRoles;

      console.log('State Value: ', this.state);
      // Validate that the Portal is in the expected state, if not forward the User to a different page.
      switch (this.state.state) {
        case AbleNetShipmentValidation_ValidationStateEnum.EnterShipmentNbr: {
          this.router.navigate(['ablenet', 'shipment-validation', this.portal.id, 'enter-info'], {});
          break;
        }
        case AbleNetShipmentValidation_ValidationStateEnum.ShipmentValidationInProgress: {
          // This is the desired state, just allow the User to continue.
          break;
        }
        default: {
          this.handleError({ error: `Unhandled State: '${this.state.state}'...` });
          this.router.navigate(['ablenet', 'shipment-validation', 'select-portal'], {});
          break;
        }
      }

      // Get the Reader that is configured to be controlled by the Portal
      let datum: InstanceData = this?.portal?.data?.find(datum => datum?.property?.property == ServicePropertyKey_Reader);
      this.reader = datum != null ? datum.object_value.reader_reference : null;
      this.refreshReaderState();

      // Start the background task to refresh the data being displayed
      setTimeout(this.refreshData.bind(this), RefreshDelayInMs);
    });
    console.log(this.overrideRoles);
  }

  /**
 * Destroys the component.  
 * Called automatically by the Angular Component Lifecycle.
 */
  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  /**
 * Refreshes the UI Display.  Constantly called by background tasks every so often
 * to keep the UI up to date with the latest changes / reads. 
 */
  private async refreshData(): Promise<void> {
    // If the Component has been destroyed, then just return as there's nothing to do
    if (this.isDestroyed) {
      return;
    }

    // Call the API to get the current state of the Portal...
    let state: ValidationInfoDto = null;
    try {
      state = await this.ablenetService.getCurrentState(this.portal.id);
    } catch (error) {
      console.error("Probelm obtaining current state of Portal.", error);
    }
    console.log(JSON.stringify(state));
    // Update the current state if needed and tell angular that it needs to update the UI (needed since this happens in the background outside it's change detection)
    if (state != null && (JSON.stringify(this.state) != JSON.stringify(state))) {
      this.state = state;
      // Set the Reader State as well since the reader is already started when you navigate to this page
      this.readerState = state.readerStatus;
      this.isReaderStateRetrieved = true;
      this.changeDetectorRef.detectChanges();
    }

    // If the component still hasn't been destroyed, then schedule the next refresh
    if (!this.isDestroyed) {
      setTimeout(this.refreshData.bind(this), RefreshDelayInMs);
    }
  }

  /**
     * Calls the API to re-obtain the reference to the Reader being controlled by the Portal.
     * This is done to obtain the "Reader State" Property Value so we can determine if the 
     * Reader has been started or not. 
     */
  private async refreshReaderState(): Promise<void> {
    // Obtain a fresh reference to the Reader using it's ID...
    let reader: ReaderInstance = null;
    try {
      reader = await this.readerService.getReaderInstanceById(this.reader?.id);
    } catch (error) {
      console.error("Problem obtaining Reader State.", error);
      return;
    }
    this.reader = reader;

    // Get the current state from the Reader Data for the "Reader Status" Property
    const datum = this.reader?.data?.find(datum => datum?.property?.property == ReaderPropertyKey_ReaderStatus);
    if (datum != null) {
      this.readerState = datum.value as AbleNetReaderStateEnum;
    }

    // Update the Flag for whether the Reader State has been retrieved or not.
    this.isReaderStateRetrieved = true;
  }

  /**
* Called by the UI when the "Start Reader" button is clicked.
* Kicks off the "Start Reader" Action in the background if no Action
* is currently being performed.
*/
  onStartReaderClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.startReader();
    }
  }

  /**
   * Performs the "Start Reader" Action
   */
  private async startReader(): Promise<void> {
    let result: any = null;
    try {
      result = await this.readerService.executeFunctionByKey(this.reader?.id, ReaderFunctionKey_StartReader, null);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    await this.refreshReaderState();
    this.isActionBeingPerformed = false;
  }

  /**
 * Called by the UI when the "Stop Reader" button is clicked.
 * Kicks off the "Stop Reader" Action in the background if no Action
 * is currently being performed.
 */
  onStopReaderClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.stopReader();
    }
  }

  /**
 * Performs the "Stop Reader" Action
 */
  private async stopReader(): Promise<void> {
    let result: any = null;
    try {
      result = await this.readerService.executeFunctionByKey(this.reader?.id, ReaderFunctionKey_StopReader, null);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    await this.refreshReaderState();
    this.isActionBeingPerformed = false;
  }

    /**
 * Called by the UI when the "Clear Reads" button is clicked.
 * Kicks off the "Clear Reads" Action in the background if no Action
 * is currently being performed.
 */
  onClearReadsClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.displayOverrideButton = false;
      this.clearReads();
    }
  }

  /**
   * Performs the "Clear Reads" Action
   */
  private async clearReads(): Promise<void> {
    let state: ValidationInfoDto = null;
    try {
      state = await this.ablenetService.clearReads(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }
    this.isActionBeingPerformed = false;
  }

  /**
 * Called by the UI when the "Clear All" button is clicked.
 * Kicks off the "Clear All" Action in the background if no Action
 * is currently being performed.
 */
  onClearAllClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.isAWarranty = false;
      this.clearAll();
    }
  }

  /**
   * Performs the "Clear All" Action
  */
  private async clearAll(): Promise<void> {
    let state: ValidationInfoDto = null;
    try {
      state = await this.ablenetService.clearAll(this.portal.id);
    } catch (error) {
      this.handleError(error);
      this.isActionBeingPerformed = false;
      return;
    }

    this.router.navigate(['ablenet', 'shipment-validation', this.portal.id, 'enter-info'], {});
    this.isActionBeingPerformed = false;
  }

/**
* Called by the UI when the "Process Shipment" button is clicked.
* Kicks off the "Process Shipment" Action in the background if no Action
* is currently being performed.
*/
  onProcessClicked(): void {
    if (!this.isActionBeingPerformed) {
      this.isActionBeingPerformed = true;
      this.indefiniteProgressDialog.show("Processing", "Updating Shipment info...");
      this.processShipment();
    }
  }
 
/**
 * Performs the "Process Shipment" Action
 */
  private async processShipment(): Promise<void> {
    const shipmentId : string = this.state.attemptDto.shipmentValidationInfo?.shipmentId;
    const packageId : string = this.state.attemptDto.shipmentValidationInfo?.packageId;
    const asset : AbleNetValidation_AssetDetailDto = this.state.asset != null ? this.state.asset : null;
    // If the Asset is null, hide the Dialog & display Error Message, also reset state
    if ( asset == null) {
      this.indefiniteProgressDialog.hide();
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE,"Asset not found !! Please restart the Reader & select a valid Asset.");
      this.isActionBeingPerformed = false;
      return;
    } else {
          try {
            await this.ablenetService.processShipment(this.portal.id, shipmentId, packageId, asset.epc, this.override, this.isAWarranty);
          } catch (error) {
            this.indefiniteProgressDialog.hide();
            this.handleError(error).then(() => {
                this.displayOverrideButton = true;
                this.isActionBeingPerformed = false;
              });
            return;
          }
    }

    this.indefiniteProgressDialog.hide();
    this.isActionBeingPerformed = false;
    this.displayOverrideDialog = false;
    this.override = false;
    this.isAWarranty = false;
    this.simpleSuccessErrorDialogComponent.showErrorDialog(SUCCESS_DIALOG_TITLE, this.state?.attemptDto?.shipmentNbr + " : Sync Successful !!");
    setTimeout(() => { this.router.navigate(['ablenet', 'shipment-validation', this.portal.id, 'enter-info']); }, 2000);  
  }

  /**
 * Returns a boolean which indicates if the Reader being controlled by the Portal
 * is started or not.  Used by the UI to disable some controls.
 */
  isReaderStarted(): boolean {
    return this.isReaderStateRetrieved && this.readerState == AbleNetReaderStateEnum.Started;
  }

  /**
   * Called when the Error Dialog is closed by the User, handles the event emitted by
   * the SimpleSuccessErrorDialog
   */
  onErrorDialogClosed() {
    this.isActionBeingPerformed = false;
    this.displayOverrideButton = true;
  }

  onOverrideClicked(): void {
    this.displayOverrideDialog = true;
    this.showOverrideDialog();
  }


  /** Display the OverRide Dilaog for User to input the Username & Password */
  showOverrideDialog() : void {
    this.isActionBeingPerformed = true;
    this.displayOverrideDialog = true;
    this.userValidationDialogComponent.show();
  }

  /**
   * 
   * @param isValid Displayed after User has clicked on the Override button 
   * in the Dialog. Check the status for login successfull or failed & proceed with next steps
   */
  onUserValidate(isValid : boolean): void {
    this.indefiniteProgressDialog.show("Processing", "Validating User Credentials...");
    if (isValid) {
      this.override = true;
      this.isActionBeingPerformed = false;
      this.displayOverrideDialog = false;
      console.log("Overriding is Successful now processing....");
      this.onProcessClicked();
    } else {
      this.override = false;
      this.indefiniteProgressDialog.hide();
      this.displayOverrideDialog = false;
      this.isActionBeingPerformed = false;
    }
  }

  /**
     * Called to handle errors when calling APIs.  
     * Currently this prints them out to the console and displays an alert to the User.
     */ 
  handleError(error: any): Promise<void> {
    console.error(`Error detected: ${error}`);

    if (error?.error != null) {
      return new Promise(resolve => {
        this.override = false;
        this.simpleSuccessErrorDialogComponent.dialogClosed.subscribe(() => resolve());
        this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
      });
    }

    const response: Response = error;
    alert(response.text());
    return Promise.resolve();
  }


}